import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Copy, TrendingUp, Users, DollarSign, Calendar, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/use-toast'
import DashboardLayout from '@/components/layout/DashboardLayout'
import StripeConnectOnboarding from '@/components/posit/StripeConnectOnboarding'

interface AffiliateCode {
  id: string
  user_id: string
  custom_code: string
  is_active: boolean
  created_at: string
  updated_at: string
}

interface AffiliateStats {
  totalReferrals: number
  activeSubscriptions: number
  paidPositsSold: number
  totalEarned: number
  availablePayout: number
  monthlyRecurring: number
  paidPositsRevenue: number
  totalClicks: number
  conversionRate: number
}

export const AffiliateDashboard: React.FC = () => {
  const { user } = useAuth()
  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const [affiliateCode, setAffiliateCode] = useState<AffiliateCode | null>(null)
  const [stats, setStats] = useState<AffiliateStats | null>(null)
  const [customCode, setCustomCode] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      loadAffiliateData()
    }
  }, [user])

  const loadAffiliateData = async () => {
    try {
      setIsLoading(true)
      setError('')
      const session = await supabase.auth.getSession()
      const token = session.data.session?.access_token
      
      if (!token) {
        throw new Error('No valid session found')
      }

      const supabaseUrl = 'https://pvnkpbsccbmbrtqczups.supabase.co'
      
      // Make all API calls in parallel for better performance
      const [accessResponse, codeResponse, statsResponse] = await Promise.all([
        fetch(`${supabaseUrl}/functions/v1/affiliate-management/access`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${supabaseUrl}/functions/v1/affiliate-management/code`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${supabaseUrl}/functions/v1/affiliate-management/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])

      // Check access first
      if (!accessResponse.ok) {
        throw new Error('Failed to check affiliate access')
      }
      const accessData = await accessResponse.json()
      setHasAccess(accessData.data.hasAccess)

      // If no access, don't load other data
      if (!accessData.data.hasAccess) {
        return
      }

      // Load affiliate code
      if (codeResponse.ok) {
        const codeData = await codeResponse.json()
        if (codeData?.data) {
          setAffiliateCode(codeData.data)
          setCustomCode(codeData.data.custom_code)
        }
      }

      // Load stats
      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        if (statsData?.data) {
          setStats(statsData.data)
        }
      }

    } catch (error) {
      setError(`Failed to load affiliate data: ${error.message}`)
      setHasAccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateCode = async () => {
    if (!customCode.trim()) {
      setError('Please enter a custom code')
      return
    }

    try {
      setIsUpdating(true)
      setError(null)

      const session = await supabase.auth.getSession()
      if (!session.data.session?.access_token) {
        throw new Error('No valid session found')
      }

      const supabaseUrl = 'https://pvnkpbsccbmbrtqczups.supabase.co'
      const response = await fetch(`${supabaseUrl}/functions/v1/affiliate-management/code`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ customCode: customCode.trim() })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create affiliate code')
      }

      const data = await response.json()

      setAffiliateCode(data.data)
      toast({
        title: "Success!",
        description: "Your affiliate code has been created successfully.",
      })
    } catch (error: any) {
      setError(error.message || 'Failed to create affiliate code')
    } finally {
      setIsUpdating(false)
    }
  }

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: "Copied!",
      description: `${type} copied to clipboard.`,
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (hasAccess === false) {
    return (
      <DashboardLayout
        title="Affiliate Program"
        description="Earn 5% commission on Posit+ subscriptions and paid Posit purchases"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              Posit+ Required
            </CardTitle>
            <CardDescription>
              The affiliate program is exclusive to Posit+ subscribers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p>
                Upgrade to Posit+ to access our affiliate program and start earning 5% commission on:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                <li>Posit+ subscription referrals</li>
                <li>Paid Posit purchases</li>
                <li>Lifetime recurring revenue from your referrals</li>
              </ul>
              <Button onClick={() => window.location.href = '/pricing'}>
                Upgrade to Posit+
              </Button>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    )
  }

  const affiliateLink = affiliateCode ? `https://tryposit.com/signup?ref=${affiliateCode.custom_code}` : ''

  return (
    <DashboardLayout
      title="Affiliate Program"
      description="Earn 5% commission on Posit+ subscriptions and paid Posit purchases from your referrals"
    >
      <div className="space-y-6">

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Total Referrals</p>
                  <p className="text-2xl font-bold">{stats.totalReferrals}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Active Subscriptions</p>
                  <p className="text-2xl font-bold">{stats.activeSubscriptions}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Total Earned</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalEarned)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Available Payout</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.availablePayout)}</p>
                  <p className="text-xs text-gray-500">$50 minimum</p>
                </div>
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Total Clicks</p>
                  <p className="text-2xl font-bold">{stats.totalClicks}</p>
                  <p className="text-xs text-gray-500">Affiliate link clicks</p>
                </div>
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Conversion Rate</p>
                  <p className="text-2xl font-bold">{stats.conversionRate}%</p>
                  <p className="text-xs text-gray-500">Clicks to signups</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Affiliate Code Management */}
      <Card>
        <CardHeader>
          <CardTitle>Your Affiliate Code</CardTitle>
          <CardDescription>
            Create a custom affiliate code to share with others. You can only have one active code.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!affiliateCode ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="customCode">Custom Code</Label>
                <Input
                  id="customCode"
                  placeholder="e.g., john_doe_posit"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  3-50 characters, letters, numbers, underscores, and hyphens only
                </p>
              </div>
              <Button onClick={handleCreateCode} disabled={isUpdating}>
                {isUpdating ? 'Creating...' : 'Create Affiliate Code'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-lg p-2">
                  {affiliateCode.custom_code}
                </Badge>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Affiliate Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      readOnly
                      value={affiliateLink}
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(affiliateLink, 'Affiliate link')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Referral Code (for sharing)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      readOnly
                      value={affiliateCode.custom_code}
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(affiliateCode.custom_code, 'Referral code')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="text-sm text-muted-foreground bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-foreground">How to use your affiliate code:</h4>
                <ul className="space-y-1">
                  <li>• Share the full link or just the code with potential users</li>
                  <li>• When they sign up using your code/link, they become your referral</li>
                  <li>• You earn 5% commission when they subscribe to Posit+ or buy paid Posits</li>
                  <li>• Commissions are held for 60 days, then available for payout at $50 minimum</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stripe Connect Payment Setup */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Setup</CardTitle>
          <CardDescription>
            Set up your bank account to receive affiliate commission payouts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StripeConnectOnboarding />
        </CardContent>
      </Card>

      {/* Additional Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Recurring Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.monthlyRecurring)}
              </p>
              <p className="text-sm text-gray-600">
                From {stats.activeSubscriptions} active subscription{stats.activeSubscriptions !== 1 ? 's' : ''}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Paid Posits Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats.paidPositsRevenue)}
              </p>
              <p className="text-sm text-gray-600">
                From {stats.paidPositsSold} paid Posit sale{stats.paidPositsSold !== 1 ? 's' : ''}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </DashboardLayout>
  )
} 